/**
 * Open stand-ins for the fonts corporate Office files use, registered under the Office family names so the
 * renderer measures and draws text with the metrics PowerPoint and Word used. The app's own font stack starts
 * with `system-ui`, so a registered "Segoe UI" never replaces the interface font. Served from
 * `public/fonts/office`, licences beside the files.
 */
const OFFICE_FONT_FACES: ReadonlyArray<{
  family: string;
  file: string;
  weight: string;
}> = [
  { family: 'Segoe UI', file: 'selawik-400.woff2', weight: '400' },
  { family: 'Segoe UI', file: 'selawik-600.woff2', weight: '600' },
  { family: 'Segoe UI', file: 'selawik-700.woff2', weight: '700' },
  { family: 'Calibri', file: 'carlito-400.ttf', weight: '400' },
  { family: 'Calibri', file: 'carlito-700.ttf', weight: '700' },
  { family: 'Montserrat', file: 'montserrat-400.ttf', weight: '400' },
  { family: 'Montserrat', file: 'montserrat-600.ttf', weight: '600' },
  { family: 'Montserrat', file: 'montserrat-700.ttf', weight: '700' },
];

/** One registration per session, shared by every preview opened after the first. */
let registration: Promise<void> | null = null;

/** Load one face into the document; a face that fails to load is skipped, since the renderer falls back. */
async function addFace(
  face: (typeof OFFICE_FONT_FACES)[number],
): Promise<void> {
  const fontFace = new FontFace(
    face.family,
    `url(/fonts/office/${face.file})`,
    { weight: face.weight },
  );
  try {
    document.fonts.add(await fontFace.load());
  } catch {
    return;
  }
}

/** Register the stand-in fonts once; resolves when every face has loaded or failed, and never rejects. */
export function registerOfficeFonts(): Promise<void> {
  registration ??= Promise.all(OFFICE_FONT_FACES.map(addFace)).then(
    () => undefined,
  );
  return registration;
}
